import {
  type ClinicalCodeWithTransformations,
  useClinicalCodeWithTransformations,
} from "@/hooks/useClinicalCodeWithTransformations";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

interface CodeChipProps {
  codeId: string;
  code: string;
  description: string;
  isExpected: boolean;
  isPresent: boolean;
}

interface ExpandableDiagnosisCodeChipProps extends CodeChipProps {
  isPrimary: boolean;
  isPositionCorrect: boolean;
  isOptional: boolean;
}

type ConditionSets = NonNullable<ClinicalCodeWithTransformations>["condition_sets"];

export const ExpandableDiagnosisCodeChip = ({
  codeId,
  code,
  description,
  isExpected,
  isPresent,
  isPrimary,
  isPositionCorrect,
  isOptional,
}: ExpandableDiagnosisCodeChipProps) => {
  const [expanded, setExpanded] = useState(false);
  const [transformationConditionSets, setTransformationConditionSets] = useState<ConditionSets>([]);

  const hasConditions = (transformationConditionSets?.flatMap((set) => set.conditions) || []).length > 0;

  const { data: clinicalCodeWithTransformations } = useClinicalCodeWithTransformations(codeId, !hasConditions);

  useEffect(() => {
    if (
      !hasConditions &&
      clinicalCodeWithTransformations?.condition_sets &&
      clinicalCodeWithTransformations.condition_sets.length > 0
    ) {
      const conditionSets = clinicalCodeWithTransformations.condition_sets;

      setTransformationConditionSets(conditionSets);
    }
  }, [clinicalCodeWithTransformations, hasConditions]);

  return (
    <div className="w-full">
      <div
        className={cn(
          "inline-flex items-center rounded-full px-3 py-1 text-sm font-medium max-w-full relative cursor-pointer w-full",
          isPresent && isExpected && isPositionCorrect
            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
            : (!isPresent || !isExpected) && !isOptional
              ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
              : isOptional && !isPresent
                ? "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100"
                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100",
        )}
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        style={{ userSelect: "none" }}
      >
        <div className="flex items-center gap-1 min-w-0">
          <span className="flex-shrink-0">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
          <span className="font-semibold shrink-0">{code}</span>
          <span className="mx-1 shrink-0">•</span>
          <span className="truncate">{description}</span>
          {isPrimary && isPositionCorrect && <span className="text-muted-foreground">(Primary)</span>}
          {isPrimary && !isPositionCorrect && <span className="text-muted-foreground">(Unexpected-primary)</span>}
          <span>{isPresent && !isExpected ? "[Unexpected] " : ""}</span>
          <span>{!isOptional && !isPrimary && !isPositionCorrect && isExpected ? "[Unexpected-position] " : ""}</span>
          <span>{!isPresent && isExpected ? "[Missing] " : ""}</span>
          {isOptional && <span className="text-muted-foreground"> (Optional)</span>}
        </div>
      </div>
      {expanded && (
        <div className="mt-2 ml-6 space-y-4">
          {transformationConditionSets && transformationConditionSets.length > 0 ? (
            transformationConditionSets.map((set, index) => (
              <div key={set.id} className="border rounded p-2 bg-muted/30">
                <div className="font-semibold mb-1">Condition Set {index + 1}</div>
                <div className="space-y-2">
                  {set.conditions.map((cond) => (
                    <div key={cond.id} className="border rounded p-2 bg-muted/50">
                      <div>
                        <span className="font-semibold">Operator:</span> {cond.operator}
                      </div>
                      <div>
                        <span className="font-semibold">Field:</span> {cond.fields?.name}
                      </div>
                      <div>
                        <span className="font-semibold">Datapoint:</span>{" "}
                        {cond.datapoints?.name || <span className="italic text-muted-foreground">None</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-muted-foreground">No transformations exist for this code</div>
          )}
        </div>
      )}
    </div>
  );
};
